// Pickup store selector: ZIP search inside Dawn's pickup-availability drawer.
// ZIP -> stores lookup runs entirely in the browser against a static theme
// asset (pickup-zip-data.json). The asset URL is rendered into the script tag's
// data-pickup-data-url attribute by Liquid. Selecting a store writes
// pickup_store_id / pickup_store_name cart attrs so the bound store carries to
// checkout. Clearing the store (or first-selecting over a delivery cart)
// empties the cart (homogeneous-cart invariant). Switching stores keeps the
// lines the NEW store also stocks and removes only the rest — the kept lines
// are already _fulfillment=pickup and rebind to the one new store, so the
// backend validation (no mix) and location filter (single store) still hold.
(function () {
  if (window.__pickupStoreSelectorInit) return;
  window.__pickupStoreSelectorInit = true;

  const log = (...args) => console.log("[pickup-store-selector]", ...args);

  const DATA_URL =
    document.querySelector("script[data-pickup-data-url]")?.dataset.pickupDataUrl || null;

  // Lazy-loaded ZIP data, fetched once and cached for the page. Compact format:
  //   { "stores": ["Name A", ...31 unique],  "zips": { "46303": [0, 3], ... } }
  // The zip values are indices into `stores` (the 31 names repeat across 2,838
  // ZIPs, so indexing keeps the asset ~5x smaller). storesForZip() rehydrates.
  let zipDataPromise = null;
  function loadZipData() {
    if (zipDataPromise) return zipDataPromise;
    if (!DATA_URL) {
      console.error("[pickup-store-selector] missing data-pickup-data-url on script tag");
      zipDataPromise = Promise.resolve({ stores: [], zips: {}, loadFailed: true });
      return zipDataPromise;
    }
    zipDataPromise = fetch(DATA_URL, { headers: { Accept: "application/json" } })
      .then((r) => {
        if (!r.ok) throw new Error(`zip data fetch responded ${r.status}`);
        return r.json();
      })
      .catch((e) => {
        console.error("[pickup-store-selector] failed to load zip data", e);
        // Don't memoize the failure — one blip would otherwise make every ZIP
        // search report "no store within 100 miles" until a full page reload.
        // Evict so the next search retries; flag THIS attempt as a load failure
        // so the search shows a retry message, not a false "no store near you".
        zipDataPromise = null;
        return { stores: [], zips: {}, loadFailed: true };
      });
    return zipDataPromise;
  }

  // Resolve a ZIP to its nearby Shopify Location names via the index.
  function storesForZip(data, zip) {
    const stores = (data && data.stores) || [];
    const idxs = (data && data.zips && data.zips[zip]) || [];
    return idxs.map((i) => stores[i]).filter((name) => name != null);
  }

  function normalizeName(name) {
    return (name || "").trim().toLowerCase();
  }

  function getDrawer(fromEl) {
    return (
      fromEl?.closest("pickup-availability-drawer") ||
      document.querySelector("pickup-availability-drawer")
    );
  }

  function setMessage(drawer, text, { error = false } = {}) {
    const el = drawer?.querySelector("[data-pickup-zip-message]");
    if (!el) return;
    el.textContent = text || "";
    el.hidden = !text;
    el.classList.toggle("pickup-zip-search__message--error", !!error);
  }

  async function handleSearch(drawer) {
    const input = drawer.querySelector("[data-pickup-zip-input]");
    const rawZip = (input?.value || "").trim();
    const zip = (rawZip.match(/^\d{5}/) || [])[0];

    if (!zip) {
      log("invalid zip entered:", JSON.stringify(rawZip));
      setMessage(drawer, "Please enter a valid 5-digit ZIP code.", { error: true });
      return;
    }

    log("searching zip:", zip);
    setMessage(drawer, "Searching…");

    const data = await loadZipData();
    if (data.loadFailed) {
      // The data didn't load — saying "no store within 100 miles" would be a
      // false claim. Leave the full list visible and ask for a retry (the
      // failed fetch was evicted above, so searching again re-attempts).
      showAllStores(drawer);
      setMessage(drawer, "Couldn't load store data — please try again.", { error: true });
      return;
    }
    const stores = storesForZip(data, zip);
    log(`zip ${zip} -> nearby stores:`, stores);
    applyFilter(drawer, zip, stores);
  }

  function showAllStores(drawer) {
    drawer.querySelectorAll("[data-pickup-store-item]").forEach((li) => (li.hidden = false));
    setMessage(drawer, "");
  }

  function hideAllStores(drawer) {
    drawer.querySelectorAll("[data-pickup-store-item]").forEach((li) => (li.hidden = true));
  }

  // Reveal every nearby store — sold-out ones included (with a "Sold out" label).
  function applyFilter(drawer, zip, nearbyStoreNames) {
    const nearby = new Set(nearbyStoreNames.map(normalizeName));
    const items = [...drawer.querySelectorAll("[data-pickup-store-item]")];

    let shown = 0;
    let inStock = 0;
    for (const li of items) {
      const isNear = nearby.has(normalizeName(li.getAttribute("data-location-name")));
      li.hidden = !isNear;
      if (isNear) {
        shown += 1;
        if (li.getAttribute("data-available") === "true") inStock += 1;
      }
    }

    if (shown === 0) {
      setMessage(drawer, `No S2 store within 100 miles of ${zip}.`, { error: true });
    } else if (inStock === 0) {
      setMessage(drawer, `Found ${shown} store${shown === 1 ? "" : "s"} near ${zip}, but this item is sold out at ${shown === 1 ? "it" : "all of them"}.`, { error: true });
    } else {
      setMessage(drawer, `Showing ${shown} store${shown === 1 ? "" : "s"} near ${zip}.`);
    }
    log(`filter applied: zip=${zip} matched=${shown} inStock=${inStock}`);
  }

  function getSavedStore() {
    try {
      return JSON.parse(localStorage.getItem("s2_pickup_store") || "null");
    } catch {
      return null;
    }
  }

  async function setStoreAttrs(id, name) {
    const res = await fetch("/cart/update.js", {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({ attributes: { pickup_store_id: id, pickup_store_name: name } }),
    });
    if (!res.ok) throw new Error(`cart/update.js responded ${res.status}`);
    return res.json();
  }

  // POST /cart/update.js with the given payload plus a Section Rendering
  // request, then re-render the cart UI in place. Shared by the clear-cart,
  // partial-keep, and clear-store flows so they can't drift.
  async function updateCartAndRender(payload) {
    const cart =
      document.querySelector("cart-drawer") || document.querySelector("cart-notification");
    const sections =
      cart && cart.getSectionsToRender ? cart.getSectionsToRender().map((s) => s.id) : [];
    const res = await fetch("/cart/update.js", {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify(
        Object.assign({}, payload, { sections, sections_url: window.location.pathname }),
      ),
    });
    if (!res.ok) throw new Error(`cart/update.js responded ${res.status}`);
    const state = await res.json();
    if (cart && cart.renderContents) cart.renderContents(state);
    return state;
  }

  // Empties the cart and (re)sets the bound-store attrs, re-rendering in place.
  async function emptyCartAndSetAttrs(id, name) {
    const res = await fetch("/cart/clear.js", {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: "{}",
    });
    if (!res.ok) throw new Error(`cart/clear.js responded ${res.status}`);
    await updateCartAndRender({ attributes: { pickup_store_id: id, pickup_store_name: name } });
  }

  // Per-variant store availability, read from the same section endpoint Dawn's
  // pickup-availability element uses (base URL taken from its data-base-url so
  // locale roots keep working). Resolves to Map<locationId, boolean>; a location
  // missing from the list means the variant isn't stocked there. Cached per
  // page load; failed fetches are evicted so a retry can succeed.
  const variantAvailabilityCache = new Map();
  function fetchVariantStoreAvailability(variantId) {
    const key = String(variantId);
    if (variantAvailabilityCache.has(key)) return variantAvailabilityCache.get(key);
    const baseUrl = document.querySelector("pickup-availability")?.dataset.baseUrl || "/";
    const promise = fetch(`${baseUrl}variants/${key}/?section_id=pickup-availability`)
      .then((r) => {
        if (!r.ok) throw new Error(`pickup-availability section responded ${r.status}`);
        return r.text();
      })
      .then((html) => {
        const doc = new DOMParser().parseFromString(html, "text/html");
        const byLocation = new Map();
        doc.querySelectorAll("[data-pickup-store-item]").forEach((item) => {
          byLocation.set(
            item.getAttribute("data-location-id"),
            item.getAttribute("data-available") === "true",
          );
        });
        return byLocation;
      });
    variantAvailabilityCache.set(key, promise);
    promise.catch(() => variantAvailabilityCache.delete(key));
    return promise;
  }

  // Splits cart lines by availability at a location. Only an explicit
  // data-available="true" row keeps a line; unknown:true means a lookup failed
  // and the caller should fall back to clearing the whole cart (never keep a
  // line we couldn't verify).
  async function partitionCartLines(locationId, items) {
    const wanted = String(locationId);
    const uniqueIds = [...new Set(items.map((line) => String(line.variant_id)))];
    try {
      const maps = await Promise.all(uniqueIds.map(fetchVariantStoreAvailability));
      const stocked = new Set(uniqueIds.filter((id, i) => maps[i].get(wanted) === true));
      return {
        unknown: false,
        unavailable: items.filter((line) => !stocked.has(String(line.variant_id))),
      };
    } catch (e) {
      console.error("[pickup-store-selector] availability check failed", e);
      return { unknown: true, unavailable: [] };
    }
  }

  // Partial keep on store switch: drop only the given lines and (re)bind the
  // store attrs in one /cart/update.js call.
  async function removeLinesAndSetAttrs(id, name, lines) {
    const updates = {};
    for (const line of lines) updates[line.key] = 0;
    await updateCartAndRender({
      updates,
      attributes: { pickup_store_id: id, pickup_store_name: name },
    });
  }

  // Click Dawn's native close button so its focus-restore / ESC handlers fire.
  function closeDrawer(fromEl, delayMs = 700) {
    const drawer = getDrawer(fromEl);
    if (!drawer) return;
    setTimeout(() => {
      const closeBtn = drawer.querySelector(".pickup-availability-drawer-button");
      if (closeBtn) closeBtn.click();
      else drawer.removeAttribute("open");
    }, delayMs);
  }

  async function selectStore(li) {
    const newId = li.getAttribute("data-location-id");
    const newName = li.getAttribute("data-location-name");
    if (!newId) {
      console.error("[pickup-store-selector] selected item has no location id", li);
      return;
    }

    // Match by location id, not display name — an admin rename must not read as a
    // "switch" (which would needlessly clear the cart). id is stable; both sides
    // come from the same source (saved store {id} <-> drawer data-location-id).
    const prev = getSavedStore();
    const isSwitch = !!prev && String(prev.id) !== String(newId);
    const isReselect = !!prev && String(prev.id) === String(newId);

    if (isReselect) {
      try {
        await setStoreAttrs(newId, newName);
        // Keep localStorage in step with the attrs we just wrote: after an admin
        // rename the drawer's name is the fresh one, and resyncStoreAttrs treats
        // localStorage as authoritative — a stale saved name would be written
        // BACK over the attrs on the next load, breaking the backend's
        // exact-location-name match at checkout.
        try {
          localStorage.setItem("s2_pickup_store", JSON.stringify({ id: newId, name: newName }));
        } catch (_) {}
      } catch (e) {
        console.error("[pickup-store-selector] failed to re-sync store attrs", e);
      }
      markSelected(li);
      closeDrawer(li);
      return;
    }

    let cartItems = [];
    let cartKnown = false;
    try {
      const cart = await fetch("/cart.js", { headers: { Accept: "application/json" } }).then((r) => r.json());
      cartItems = cart.items || [];
      cartKnown = true;
    } catch (_) {}

    // "set"     -> keep the cart as-is, just (re)bind the store attrs
    // "partial" -> remove the `unavailable` lines, keep the rest, bind attrs
    // "clear"   -> empty the cart, bind attrs
    let action = "set";
    let unavailable = [];
    if (!cartKnown) {
      // Couldn't inspect the cart — clear it rather than risk binding pickup
      // attrs onto unverified (possibly ship-stamped) lines. A no-op on an
      // actually-empty cart; if the network is truly down the clear fails into
      // the error return below and nothing is saved.
      action = "clear";
    } else if (prev == null && cartItems.length > 0) {
      // First select over a delivery cart: those are ship-stamped lines and
      // can't carry into pickup mode (fix 3.2).
      action = "clear";
    } else if (isSwitch && cartItems.length > 0) {
      const result = await partitionCartLines(newId, cartItems);
      unavailable = result.unavailable;
      if (result.unknown || unavailable.length === cartItems.length) action = "clear";
      else if (unavailable.length > 0) action = "partial";
    }

    log(`selecting store (cart action: ${action})`, {
      newId,
      newName,
      prev: prev && prev.name,
      removing: unavailable.map((line) => line.product_title),
    });

    try {
      if (action === "clear") await emptyCartAndSetAttrs(newId, newName);
      else if (action === "partial") await removeLinesAndSetAttrs(newId, newName, unavailable);
      else await setStoreAttrs(newId, newName);
    } catch (e) {
      console.error("[pickup-store-selector] failed to save store", e);
      return;
    }

    try {
      localStorage.setItem("s2_pickup_store", JSON.stringify({ id: newId, name: newName }));
    } catch (_) {}
    setStoreFlag();

    let message = null;
    if (isSwitch) {
      if (action === "set") {
        message = `Switched to ${newName}. Everything in your cart is available here.`;
      } else if (action === "partial") {
        const dropped = [...new Set(unavailable.map((line) => line.product_title))].join(", ");
        message = `Switched to ${newName}. Removed from your cart (not available here): ${dropped}.`;
      } else {
        message = `Switched to ${newName}. Your previous cart was cleared.`;
      }
    } else if (action === "clear") {
      message = `${newName} selected. Your previous cart was cleared.`;
    }
    markSelected(li, { message });

    document.dispatchEvent(new CustomEvent("pickup:store-selected"));

    if (action !== "set") {
      try {
        if (typeof publish === "function" && typeof PUB_SUB_EVENTS !== "undefined" && PUB_SUB_EVENTS.cartUpdate) {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: "pickup-store-switch" });
        }
      } catch (_) {}
    }

    closeDrawer(li);
  }

  function markSelected(selectedLi, opts) {
    const drawer = getDrawer(selectedLi);
    drawer?.querySelectorAll("[data-pickup-store-item]").forEach((li) => {
      const isSelected = li === selectedLi;
      li.classList.toggle("pickup-availability-list__item--selected", isSelected);
      const btn = li.querySelector("[data-pickup-store-select]");
      if (btn) btn.textContent = isSelected ? "Selected" : "Select this store";
    });
    const name = selectedLi.getAttribute("data-location-name");
    setMessage(
      drawer,
      (opts && opts.message) || `${name} selected. It'll be your pickup store at checkout.`,
    );
  }

  function hasSavedStore() {
    return !!getSavedStore();
  }

  // Open Dawn's store-selection drawer. Prefer the rendered opener button (so
  // Dawn's own show() + focus-trap fire); fall back to the drawer element if the
  // opener hasn't been injected yet (async fetch on PDP load).
  function openStoreDrawer() {
    const opener = document.getElementById("ShowPickupAvailabilityDrawer");
    if (opener) {
      opener.click();
      const d = document.querySelector("pickup-availability-drawer");
      const toggle = document.querySelector('[data-pickup-mode="pickup"]');
      if (d && toggle) d.focusElement = toggle;
      return;
    }
    const drawer = document.querySelector("pickup-availability-drawer");
    const toggle = document.querySelector('[data-pickup-mode="pickup"]');
    if (drawer && typeof drawer.show === "function") {
      drawer.show();
      if (toggle) drawer.focusElement = toggle; // match openDrawer(): return focus to a visible control
    } else {
      console.warn("[pickup-store-selector] store drawer not ready yet");
    }
  }

  // Keep the Delivery/In-Store-Pickup toggle's aria-pressed in sync with the
  // saved-store flag. The visual active state is handled in CSS off the same
  // <html>.s2-pickup-store-set flag, so this is a11y-only.
  function syncToggle() {
    const set = hasSavedStore();
    document.querySelectorAll("[data-pickup-mode]").forEach((btn) => {
      const isPickup = btn.getAttribute("data-pickup-mode") === "pickup";
      btn.setAttribute("aria-pressed", String(isPickup ? set : !set));
    });
  }

  // <html> flag — lets CSS conditionally show/hide store-aware UI without
  // per-element state sync.
  function setStoreFlag() {
    document.documentElement.classList.toggle("s2-pickup-store-set", hasSavedStore());
  }

  async function clearStore() {
    log("clearing saved pickup store + emptying cart (in place)");
    try {
      await emptyCartAndSetAttrs("", "");
    } catch (e) {
      // Cart is still pickup-bound — keep localStorage/UI in pickup mode so
      // they can't disagree with the cart; the shopper can just retry.
      console.error("[pickup-store-selector] failed to clear cart/store", e);
      const drawer = document.querySelector("pickup-availability-drawer");
      if (drawer) setMessage(drawer, "Couldn't switch to delivery — please try again.", { error: true });
      return;
    }

    try {
      localStorage.removeItem("s2_pickup_store");
    } catch (_) {}
    document.documentElement.classList.remove("s2-pickup-store-set");

    document.querySelectorAll("[data-pickup-store-item]").forEach((li) => {
      li.classList.remove("pickup-availability-list__item--selected");
      const btn = li.querySelector("[data-pickup-store-select]");
      if (btn) btn.textContent = "Select this store";
    });
    const drawer = document.querySelector("pickup-availability-drawer");
    if (drawer) setMessage(drawer, "Store cleared — you're shopping for delivery again.");

    try {
      if (typeof publish === "function" && typeof PUB_SUB_EVENTS !== "undefined" && PUB_SUB_EVENTS.cartUpdate) {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: "pickup-store-clear" });
      }
    } catch (_) {}
    document.dispatchEvent(new CustomEvent("pickup:store-cleared"));
  }

  // Delegated on document because Dawn re-renders the drawer markup on variant change.
  document.addEventListener("click", (e) => {
    // Delivery / In-Store Pickup toggle (above the buy buttons).
    const modeBtn = e.target.closest("[data-pickup-mode]");
    if (modeBtn) {
      e.preventDefault();
      const mode = modeBtn.getAttribute("data-pickup-mode");
      if (mode === "pickup") {
        // Always open the drawer so the customer can choose/change their store.
        openStoreDrawer();
      } else if (mode === "delivery") {
        // Switching to delivery drops the bound store (and empties the cart per
        // the homogeneous-cart rule). No-op if already shopping for delivery.
        if (hasSavedStore()) clearStore();
      }
      return;
    }

    const submitBtn = e.target.closest("[data-pickup-zip-submit]");
    if (submitBtn) {
      e.preventDefault();
      const drawer = getDrawer(submitBtn);
      if (drawer) handleSearch(drawer);
      return;
    }

    const selectBtn = e.target.closest("[data-pickup-store-select]");
    if (selectBtn) {
      e.preventDefault();
      const li = selectBtn.closest("[data-pickup-store-item]");
      if (li) selectStore(li);
    }
  });

  document.addEventListener("keydown", (e) => {
    if (e.key !== "Enter") return;
    const input = e.target.closest("[data-pickup-zip-input]");
    if (!input) return;
    e.preventDefault();
    const drawer = getDrawer(input);
    if (drawer) handleSearch(drawer);
  });

  // Typing: 0 digits -> show all, 1-4 -> hide all, 5 -> auto-search.
  document.addEventListener("input", (e) => {
    const input = e.target.closest("[data-pickup-zip-input]");
    if (!input) return;
    const drawer = getDrawer(input);
    if (!drawer) return;

    const digits = (input.value || "").replace(/\D/g, "");
    if (digits.length === 0) {
      showAllStores(drawer);
    } else if (digits.length < 5) {
      hideAllStores(drawer);
      setMessage(drawer, "");
    } else {
      handleSearch(drawer);
    }
  });

  // Two-way repair between localStorage and the cart's store attrs:
  // - saved store but stale/missing attrs (cart expired or completed an order)
  //   -> rewrite the attrs from localStorage;
  // - pickup-stamped cart but NO saved store (cleared storage, new device, or
  //   a failed clearStore on another page) -> restore localStorage from the
  //   attrs so the gate/UI can't fail-open against a store-less pickup cart.
  async function resyncStoreAttrs() {
    const saved = getSavedStore();
    try {
      const cart = await fetch("/cart.js", { headers: { Accept: "application/json" } }).then((r) => r.json());
      const attrs = cart.attributes || {};
      const attrName = attrs.pickup_store_name || "";
      const attrId = attrs.pickup_store_id || "";
      if (saved) {
        if (normalizeName(attrName) !== normalizeName(saved.name)) {
          await setStoreAttrs(saved.id, saved.name);
          // Tell the gate the binding changed so it re-reads the cart and
          // repaints (it fetched /cart.js on init, possibly before this repair).
          document.dispatchEvent(new CustomEvent("pickup:store-selected"));
        }
        return;
      }
      const hasPickupLines = (cart.items || []).some(
        (line) => line.properties && line.properties._fulfillment === "pickup",
      );
      if (attrId && attrName && hasPickupLines) {
        log("restoring saved store from cart attrs", { id: attrId, name: attrName });
        try {
          localStorage.setItem("s2_pickup_store", JSON.stringify({ id: attrId, name: attrName }));
        } catch (_) {}
        setStoreFlag();
        syncToggle();
        // Same as above: without this the gate keeps painting from its pre-heal
        // state (no saved store) until some unrelated trigger fires.
        document.dispatchEvent(new CustomEvent("pickup:store-selected"));
      }
    } catch (e) {
      console.error("[pickup-store-selector] failed to re-sync store attrs", e);
    }
  }

  document.addEventListener("pickup:store-selected", syncToggle);
  document.addEventListener("pickup:store-cleared", syncToggle);

  // The gate's "Switch back to delivery" button (its blocked-add notice, incl.
  // exempt PDPs where the Delivery toggle is suppressed) asks us to run the real
  // clear path — empties the pickup cart + drops the store binding. Guarded like
  // the Delivery-toggle path: clearStore() empties the WHOLE cart, so never run
  // it unless pickup state actually exists (saved store, or — for the
  // cleared-localStorage case — a cart that carries pickup attrs/lines).
  document.addEventListener("pickup:request-clear", async () => {
    if (hasSavedStore()) return clearStore();
    try {
      const cart = await fetch("/cart.js", { headers: { Accept: "application/json" } }).then((r) => r.json());
      const attrs = cart.attributes || {};
      const pickupBound =
        !!attrs.pickup_store_id ||
        (cart.items || []).some((line) => line.properties && line.properties._fulfillment === "pickup");
      if (pickupBound) clearStore();
    } catch (e) {
      console.error("[pickup-store-selector] request-clear probe failed", e);
    }
  });

  setStoreFlag();
  syncToggle();
  // Skip the cart probe on pure content pages with no saved store and no pickup
  // UI — nothing to re-sync there. A returning shopper with a pickup cart but
  // cleared storage gets healed on the next product/cart page instead.
  if (
    getSavedStore() ||
    document.querySelector('form[data-type="add-to-cart-form"]') ||
    document.querySelector("pickup-availability")
  ) {
    resyncStoreAttrs();
  }
  log("initialized");
})();
